<template>
  <ExpansionPanels
    v-faq-view-tracker
    class="frequently-asked-questions"
    item-scope
    item-type="https://schema.org/FAQPage"
  >
    <template #title>
      <h2 class="frequently-asked-questions__title">{{ t('title') }}</h2>
    </template>

    <template #default>
      <Item
        v-faq-item-click-tracker="{ question: t('paymentAbroad.question') }"
        :open="openSection[0]"
        @toggled="toggle(0, $event)"
      >
        <template #title>{{ t('paymentAbroad.question') }}</template>
        <template #default>
          <div class="frequently-asked-questions__item-content">
            <p v-html="t('paymentAbroad.answerHtml')" />
          </div>
        </template>
      </Item>

      <Item
        v-faq-item-click-tracker="{ question: t('askGuide.question') }"
        :open="openSection[1]"
        @toggled="toggle(1, $event)"
      >
        <template #title>{{ t('askGuide.question') }}</template>
        <template #default>
          <div class="frequently-asked-questions__item-content">
            <p>{{ t('askGuide.answer') }}</p>
          </div>
        </template>
      </Item>

      <Item
        v-faq-item-click-tracker="{ question: t('howToPay.question') }"
        :open="openSection[2]"
        @toggled="toggle(2, $event)"
      >
        <template #title>{{ t('howToPay.question') }}</template>
        <template #default>
          <div class="frequently-asked-questions__item-content">
            <p>{{ t('howToPay.intro') }}</p>

            <ol>
              <li>
                <strong>{{ t('howToPay.prepaidTitle') }}</strong> {{ t('howToPay.prepaidText') }}
              </li>
              <li>
                <strong>{{ t('howToPay.payGuideTitle') }}</strong> {{ t('howToPay.payGuideBeforeLink') }}
                <AppLink to="/tours/" target="_blank">{{ t('howToPay.tourPageLink') }}</AppLink>
                {{ t('howToPay.payGuideAfterLink') }}
              </li>
            </ol>

            <p>{{ t('howToPay.methodsIntro') }}</p>

            <ul>
              <li>
                <strong>{{ t('howToPay.russianCardTitle') }}</strong> {{ t('howToPay.russianCardText') }}
              </li>
              <li>
                <strong>{{ t('howToPay.foreignCardTitle') }}</strong> {{ t('howToPay.foreignCardText') }}
              </li>
            </ul>
          </div>
        </template>
      </Item>

      <Item
        v-faq-item-click-tracker="{ question: t('cancelled.question') }"
        :open="openSection[3]"
        @toggled="toggle(3, $event)"
      >
        <template #title>{{ t('cancelled.question') }}</template>
        <template #default>
          <div class="frequently-asked-questions__item-content">
            <p>{{ t('cancelled.answer') }}</p>
          </div>
        </template>
      </Item>

      <Item
        v-faq-item-click-tracker="{ question: t('refund.question') }"
        :open="openSection[4]"
        @toggled="toggle(4, $event)"
      >
        <template #title>{{ t('refund.question') }}</template>
        <template #default>
          <div class="frequently-asked-questions__item-content">
            <p>
              {{ t('refund.beforeLink') }}
              <!-- Абсолютная ссылка на страницу политики отмены (прод-домен, вне этого МФ). -->
              <AppLink
                to="https://experience.tripster.ru/about/cancellation_policy/"
                target="_blank"
                rel="noreferrer nofollow"
                >{{ t('refund.policyLink') }}</AppLink
              >{{ t('refund.afterLink') }}
            </p>
            <p>{{ t('refund.howToCancel') }}</p>
          </div>
        </template>
      </Item>
    </template>
  </ExpansionPanels>
</template>

<script lang="ts" setup>
import { useExpansionPanelsController, ExpansionPanels, Item } from '@experience/core-expansion-panels-ui';
import { useI18n } from '@experience/i18n';

import ru from '@/features/frequentlyAskedQuestions/i18n/ru';
import { useAnalytics } from '@/shared/lib/analytics';

type MessageSchema = typeof ru;
const { t } = useI18n<{ message: MessageSchema }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const { openSection, toggle } = useExpansionPanelsController();
const { useViewTracker, useClickTracker } = useAnalytics();
const vFaqViewTracker = useViewTracker('main:faq.view');
const vFaqItemClickTracker = useClickTracker('main:faq.click');
</script>

<style lang="scss" scoped>
.frequently-asked-questions__title {
  font-size: inherit;
  font-style: inherit;
  font-weight: inherit;
  line-height: inherit;

  @include typography.base-800-750-extra;

  @include adaptivity.mobile {
    @include typography.base-700-500-extra;
  }
}

.frequently-asked-questions__item-content {
  p,
  div {
    padding: 0;
    margin: 0;
  }

  ol,
  ul {
    display: flex;
    flex-direction: column;
    gap: var(--alias-space-16);
    padding-inline-start: var(--alias-space-24);
    padding-bottom: var(--alias-space-16);
    margin: var(--alias-space-16) 0 0;

    &:last-child {
      padding-bottom: 0;
    }

    li {
      &::marker {
        @include typography.base-500-400-semi;

        @include adaptivity.mobile {
          @include typography.base-400-400-semi;
        }
      }
    }
  }

  ol {
    list-style-type: decimal;
  }

  ul {
    list-style-type: disc;
  }

  strong {
    @include typography.base-500-400-semi;

    @include adaptivity.mobile {
      @include typography.base-400-400-semi;
    }
  }

  a,
  :deep(a) {
    color: var(--alias-color-text-default);
    text-decoration: none;
    cursor: pointer !important;
    border-bottom: 1px var(--alias-color-text-subtler) solid;
  }

  @include typography.base-500-400-regular;

  @include adaptivity.mobile {
    gap: var(--alias-space-8);

    @include typography.base-400-400-regular;
  }
}
</style>
